import { createSetDataStore, } from './set-data.js';
const SUPPORTED_APP_LIFECYCLES = new Set([
    'onError',
    'onHide',
    'onLaunch',
    'onShow',
    'onUnhandledRejection',
]);
const UNSUPPORTED_APP_LIFECYCLES = new Set(['onPageNotFound', 'onThemeChange']);
const SUPPORTED_PAGE_LIFECYCLES = new Set([
    'onHide',
    'onLoad',
    'onPageScroll',
    'onPullDownRefresh',
    'onReady',
    'onReachBottom',
    'onShow',
    'onShareAppMessage',
    'onUnload',
]);
const UNSUPPORTED_PAGE_LIFECYCLES = new Set([
    'onAddToFavorites',
    'onResize',
    'onSaveExitState',
    'onShareTimeline',
    'onTabItemTap',
]);
const APP_INSTANCE_KEYS = new Set([
    'globalData',
    'hide',
    'launch',
    'lifecycleState',
    'methods',
    'reportError',
    'reportUnhandledRejection',
    'show',
]);
const PAGE_INSTANCE_KEYS = new Set([
    'createSelectorQuery',
    'data',
    'lifecycleState',
    'methods',
    'pageScroll',
    'query',
    'readyCalled',
    'pullDownRefresh',
    'reachBottom',
    'route',
    'setData',
    'shareAppMessage',
    'whenSettled',
]);
const UNSAFE_DATA_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const MAX_DATA_DEPTH = 100;
const PAGE_LIFECYCLE_CONTROLLERS = new WeakMap();
export class RuntimeInstanceError extends Error {
    code;
    remediation;
    constructor(code, message, remediation) {
        super(`${code} ${message}`);
        this.name = 'RuntimeInstanceError';
        this.code = code;
        this.remediation = remediation;
    }
}
function definitionError(message, remediation) {
    return new RuntimeInstanceError('WF7201', message, remediation);
}
function unsupportedOption(kind, key) {
    return new RuntimeInstanceError('WF7202', `${kind} option ${key} is outside the implemented runtime subset or conflicts with the instance facade`, `Remove ${key}, rename the custom method, or wait until its cataloged ${kind} runtime capability is implemented.`);
}
function transitionError(kind, operation, state) {
    return new RuntimeInstanceError('WF7203', `Cannot ${operation} ${kind} while it is ${state}`, 'Use the router and bootstrap lifecycle order instead of invoking lifecycle transitions out of sequence.');
}
export function getWebPageLifecycle(page) {
    const controller = PAGE_LIFECYCLE_CONTROLLERS.get(page);
    if (controller === undefined) {
        throw definitionError('Page lifecycle controller requires an instance created by createWebPage', 'Pass the WebPageInstance returned by a Wefat page definition.');
    }
    return controller;
}
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === null || prototype === Object.prototype;
}
function cloneDataValue(value, path, ancestors, depth) {
    if (value === null
        || typeof value === 'boolean'
        || typeof value === 'string'
        || (typeof value === 'number' && Number.isFinite(value))) {
        return value;
    }
    if (depth > MAX_DATA_DEPTH) {
        throw definitionError(`Runtime data exceeds the supported nesting depth at ${path}`, `Keep App.globalData and Page.data at or below ${MAX_DATA_DEPTH} nested containers.`);
    }
    if (value === null || typeof value !== 'object') {
        throw definitionError(`Runtime data contains a non-serializable value at ${path}: ${typeof value}`, 'Use null, booleans, finite numbers, strings, arrays, and plain objects for initial runtime data.');
    }
    if (ancestors.has(value)) {
        throw definitionError(`Runtime data contains a cycle at ${path}`, 'Remove circular references from App.globalData or Page.data before registration.');
    }
    const clone = Array.isArray(value)
        ? new Array(value.length)
        : {};
    if (!Array.isArray(value) && !isPlainRecord(value)) {
        throw definitionError(`Runtime data contains a non-plain object at ${path}`, 'Convert class instances, dates, maps, sets, and host objects into JSON-like data before registration.');
    }
    ancestors.add(value);
    for (const key of Object.keys(value)) {
        if (UNSAFE_DATA_KEYS.has(key)) {
            throw definitionError(`Runtime data contains an unsafe key at ${path}.${key}`, 'Rename __proto__, prototype, and constructor fields before registration.');
        }
        const descriptor = Object.getOwnPropertyDescriptor(value, key);
        if (descriptor === undefined || !('value' in descriptor)) {
            throw definitionError(`Runtime data contains an accessor at ${path}.${key}`, 'Replace data getters and setters with plain data properties before registration.');
        }
        const child = cloneDataValue(descriptor.value, `${path}.${key}`, ancestors, depth + 1);
        Object.defineProperty(clone, key, {
            configurable: true,
            enumerable: true,
            value: child,
            writable: true,
        });
    }
    ancestors.delete(value);
    return clone;
}
function cloneDataRoot(value, label) {
    if (!isPlainRecord(value)) {
        throw definitionError(`${label} must be a plain object`, `Initialize ${label} with a JSON-like object before registration.`);
    }
    return cloneDataValue(value, label, new Set(), 0);
}
function readOption(options, key) {
    const descriptor = Object.getOwnPropertyDescriptor(options, key);
    if (descriptor === undefined) {
        return { exists: false, value: undefined };
    }
    if (!('value' in descriptor)) {
        throw definitionError(`Runtime options contain an accessor property: ${key}`, 'Replace option getters and setters with direct values or functions before registration.');
    }
    return { exists: true, value: descriptor.value };
}
function snapshotOptions(options) {
    const snapshot = Object.create(null);
    for (const key of Object.keys(options)) {
        const { value } = readOption(options, key);
        Object.defineProperty(snapshot, key, {
            configurable: false,
            enumerable: true,
            value,
            writable: false,
        });
    }
    return Object.freeze(snapshot);
}
function normalizePageRenderer(renderer) {
    if (!isPlainRecord(renderer)) {
        throw definitionError('Page renderer adapter must be a plain object', 'Pass an adapter with direct makeReactive and flush function properties.');
    }
    const makeReactive = readOption(renderer, 'makeReactive').value;
    const flush = readOption(renderer, 'flush').value;
    if (typeof makeReactive !== 'function' || typeof flush !== 'function') {
        throw definitionError('Page renderer adapter requires makeReactive and flush functions', 'Provide a synchronous reactive-data wrapper and a render-flush function such as Vue nextTick.');
    }
    return Object.freeze({
        makeReactive: makeReactive,
        flush: flush,
    });
}
function collectFunctions(kind, options, dataKey, supportedLifecycles, unsupportedLifecycles, instanceKeys) {
    const lifecycles = Object.create(null);
    const methods = Object.create(null);
    for (const key of Object.keys(options)) {
        if (key === dataKey) {
            readOption(options, key);
            continue;
        }
        const { value } = readOption(options, key);
        if (unsupportedLifecycles.has(key)) {
            throw unsupportedOption(kind, key);
        }
        if (supportedLifecycles.has(key)) {
            if (typeof value !== 'function') {
                throw definitionError(`${kind} lifecycle ${key} must be a function`, `Replace ${key} with a function or remove it from the ${kind} options object.`);
            }
            lifecycles[key] = value;
            continue;
        }
        if (instanceKeys.has(key) || typeof value !== 'function') {
            throw unsupportedOption(kind, key);
        }
        methods[key] = value;
    }
    return {
        lifecycles: Object.freeze(lifecycles),
        methods: Object.freeze(methods),
    };
}
function normalizeQuery(query) {
    if (query === undefined) {
        return Object.freeze({});
    }
    if (!isPlainRecord(query)) {
        throw definitionError('Route query must be a plain object', 'Pass route query values as one plain string record.');
    }
    const normalized = {};
    for (const key of Object.keys(query)) {
        const descriptor = Object.getOwnPropertyDescriptor(query, key);
        if (descriptor === undefined || !('value' in descriptor) || typeof descriptor.value !== 'string') {
            throw definitionError(`Route query value must be a string: ${key}`, 'Decode browser query parameters into strings before creating a page or app entry context.');
        }
        Object.defineProperty(normalized, key, {
            configurable: false,
            enumerable: true,
            value: descriptor.value,
            writable: false,
        });
    }
    return Object.freeze(normalized);
}
export function normalizeAppEntry(options) {
    if (typeof options.path !== 'string' || options.path.length === 0) {
        throw definitionError('Application entry path must be a non-empty string', 'Pass the normalized registered Mini Program route as the application entry path.');
    }
    return Object.freeze({ path: options.path, query: normalizeQuery(options.query) });
}
function invoke(lifecycle, instance, args = []) {
    return lifecycle === undefined ? undefined : Reflect.apply(lifecycle, instance, args);
}
function bindMethods(instance, source) {
    const bound = Object.create(null);
    for (const [name, method] of Object.entries(source)) {
        const wrapper = (...args) => Reflect.apply(method, instance, args);
        bound[name] = wrapper;
        Object.defineProperty(instance, name, {
            configurable: false,
            enumerable: true,
            value: wrapper,
            writable: false,
        });
    }
    return Object.freeze(bound);
}
export function createWebApp(options) {
    if (!isPlainRecord(options)) {
        throw definitionError('App options must be a plain object', 'Call createWebApp with the original App option object.');
    }
    const validatedOptions = snapshotOptions(options);
    const globalDataOption = readOption(validatedOptions, 'globalData');
    const globalDataTemplate = cloneDataRoot(globalDataOption.exists ? globalDataOption.value : {}, 'App.globalData');
    const collected = collectFunctions('App', validatedOptions, 'globalData', SUPPORTED_APP_LIFECYCLES, UNSUPPORTED_APP_LIFECYCLES, APP_INSTANCE_KEYS);
    return Object.freeze({
        kind: 'app',
        options: validatedOptions,
        createInstance() {
            const globalData = cloneDataRoot(globalDataTemplate, 'App.globalData');
            let state = 'created';
            let currentEntry = null;
            const instance = {};
            const methods = bindMethods(instance, collected.methods);
            Object.defineProperties(instance, {
                globalData: { enumerable: true, value: globalData },
                lifecycleState: { enumerable: true, get: () => state },
                methods: { enumerable: true, value: methods },
                launch: {
                    enumerable: true,
                    value(entry) {
                        if (state !== 'created') {
                            throw transitionError('app', 'launch', state);
                        }
                        currentEntry = normalizeAppEntry(entry);
                        state = 'launched';
                        invoke(collected.lifecycles.onLaunch, instance, [currentEntry]);
                        return currentEntry;
                    },
                },
                show: {
                    enumerable: true,
                    value(entry) {
                        if (state !== 'launched' && state !== 'hidden') {
                            throw transitionError('app', 'show', state);
                        }
                        currentEntry = entry === undefined ? currentEntry : normalizeAppEntry(entry);
                        if (currentEntry === null) {
                            throw transitionError('app', 'show without an entry', state);
                        }
                        state = 'visible';
                        invoke(collected.lifecycles.onShow, instance, [currentEntry]);
                    },
                },
                hide: {
                    enumerable: true,
                    value() {
                        if (state !== 'visible') {
                            throw transitionError('app', 'hide', state);
                        }
                        state = 'hidden';
                        invoke(collected.lifecycles.onHide, instance);
                    },
                },
                reportError: {
                    enumerable: true,
                    value(error) {
                        const message = error instanceof Error ? error.message : String(error);
                        invoke(collected.lifecycles.onError, instance, [message]);
                    },
                },
                reportUnhandledRejection: {
                    enumerable: true,
                    value(reason, promise) {
                        invoke(collected.lifecycles.onUnhandledRejection, instance, [{ reason, promise }]);
                    },
                },
            });
            return instance;
        },
    });
}
export function createWebPage(options, metadata) {
    if (!isPlainRecord(options)) {
        throw definitionError('Page options must be a plain object', 'Call createWebPage with the original Page option object.');
    }
    if (typeof metadata.route !== 'string' || metadata.route.length === 0) {
        throw definitionError('Page metadata route must be a non-empty string', 'Pass the normalized registered route when transforming Page(...).');
    }
    const validatedOptions = snapshotOptions(options);
    const dataOption = readOption(validatedOptions, 'data');
    const dataTemplate = cloneDataRoot(dataOption.exists ? dataOption.value : {}, 'Page.data');
    const collected = collectFunctions('Page', validatedOptions, 'data', SUPPORTED_PAGE_LIFECYCLES, UNSUPPORTED_PAGE_LIFECYCLES, PAGE_INSTANCE_KEYS);
    const frozenMetadata = Object.freeze({ ...metadata });
    function instantiate(renderer, flush) {
        const initialData = cloneDataRoot(dataTemplate, 'Page.data');
        const normalizedRenderer = renderer === undefined ? undefined : normalizePageRenderer(renderer);
        const data = normalizedRenderer === undefined
            ? initialData
            : normalizedRenderer.makeReactive(initialData);
        if (!isPlainRecord(data)) {
            throw definitionError('Page renderer adapter returned an invalid reactive data root', 'Return the supplied plain data object or a plain-object-compatible reactive proxy.');
        }
        const renderFlush = normalizedRenderer?.flush ?? flush;
        const store = createSetDataStore(data, renderFlush === undefined ? {} : { flush: renderFlush });
        let state = 'created';
        let query = Object.freeze({});
        let readyCalled = false;
        const instance = {};
        const methods = bindMethods(instance, collected.methods);
        const lifecycle = Object.freeze({
            load(routeQuery) {
                if (state !== 'created') {
                    throw transitionError('page', 'load', state);
                }
                query = normalizeQuery(routeQuery);
                state = 'loaded';
                invoke(collected.lifecycles.onLoad, instance, [query]);
            },
            show() {
                if (state !== 'loaded' && state !== 'hidden') {
                    throw transitionError('page', 'show', state);
                }
                state = 'visible';
                invoke(collected.lifecycles.onShow, instance);
            },
            ready() {
                if (state !== 'visible' || readyCalled) {
                    throw transitionError('page', 'mark ready', state);
                }
                readyCalled = true;
                invoke(collected.lifecycles.onReady, instance);
            },
            hide() {
                if (state !== 'visible') {
                    throw transitionError('page', 'hide', state);
                }
                state = 'hidden';
                invoke(collected.lifecycles.onHide, instance);
            },
            unload() {
                if (state !== 'loaded' && state !== 'visible' && state !== 'hidden') {
                    throw transitionError('page', 'unload', state);
                }
                state = 'unloaded';
                invoke(collected.lifecycles.onUnload, instance);
            },
        });
        Object.defineProperties(instance, {
            route: { enumerable: true, value: frozenMetadata.route },
            query: { enumerable: true, get: () => query },
            data: { enumerable: true, value: data },
            lifecycleState: { enumerable: true, get: () => state },
            readyCalled: { enumerable: true, get: () => readyCalled },
            methods: { enumerable: true, value: methods },
            setData: {
                enumerable: true,
                value(patch, callback) {
                    if (state === 'unloaded') {
                        throw transitionError('page', 'setData', state);
                    }
                    store.setData(patch, callback);
                },
            },
            pageScroll: {
                enumerable: true,
                value(scrollTop) {
                    if (state !== 'visible') {
                        throw transitionError('page', 'run page-scroll', state);
                    }
                    if (!Number.isFinite(scrollTop) || scrollTop < 0) {
                        throw definitionError('Page scrollTop must be a finite non-negative number', 'Pass the normalized browser viewport scroll position to pageScroll.');
                    }
                    invoke(collected.lifecycles.onPageScroll, instance, [Object.freeze({ scrollTop })]);
                },
            },
            pullDownRefresh: {
                enumerable: true,
                value() {
                    if (state !== 'visible') {
                        throw transitionError('page', 'run pull-down refresh', state);
                    }
                    invoke(collected.lifecycles.onPullDownRefresh, instance);
                },
            },
            reachBottom: {
                enumerable: true,
                value() {
                    if (state !== 'visible') {
                        throw transitionError('page', 'run reach-bottom', state);
                    }
                    invoke(collected.lifecycles.onReachBottom, instance);
                },
            },
            shareAppMessage: {
                enumerable: true,
                value(event) {
                    if (state === 'created' || state === 'unloaded') {
                        throw transitionError('page', 'create a share message', state);
                    }
                    return invoke(collected.lifecycles.onShareAppMessage, instance, [event]);
                },
            },
            whenSettled: { enumerable: true, value: () => store.whenSettled() },
        });
        const page = instance;
        PAGE_LIFECYCLE_CONTROLLERS.set(page, lifecycle);
        return page;
    }
    return Object.freeze({
        kind: 'page',
        metadata: frozenMetadata,
        options: validatedOptions,
        createInstance(flush) {
            return instantiate(undefined, flush);
        },
        createRenderedInstance(renderer) {
            return instantiate(renderer, undefined);
        },
    });
}
export function bootstrapFirstPage(input) {
    if (input.flush !== undefined && input.renderer !== undefined) {
        throw new RuntimeInstanceError('WF7202', 'First-page bootstrap cannot accept both a bare flush function and a renderer adapter', 'Pass renderer for reactive UI integration, or flush for a framework-independent runtime instance.');
    }
    const app = input.app.createInstance();
    const page = input.renderer === undefined
        ? input.page.createInstance(input.flush)
        : input.page.createRenderedInstance(input.renderer);
    const entry = app.launch(input.entry);
    const lifecycle = getWebPageLifecycle(page);
    lifecycle.load(entry.query);
    lifecycle.show();
    app.show();
    return Object.freeze({ app, page, entry });
}
//# sourceMappingURL=instance-factory.js.map